import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getShipTo, GetShipToApiParameter } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import shareProduct from "@insite/client-framework/Store/CommonHandlers/ShareProduct";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import FileUpload from "@insite/mobius/FileUpload";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Upload from "@insite/mobius/Icons/Upload";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PhoneNumberField, { PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import useConstructor from "@preferredMedical/Components/Misc/UseConstructor";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { SendPriceMatchRequest } from "@preferredMedical/Models/ProductDetails/PriceMatch/SendPriceMatchRequest";
import { checkStatusCode, sendPriceMatch } from "@preferredMedical/Services/ProductService";
import validateReCaptcha from "@preferredMedical/Store/Components/Recapcha/Handlers/ValidateReCaptcha";
import React, { RefObject } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    text?: string;
    extendedStyles?: ProductPriceMatchStyles;
    emailRecipients: string;
    consentDescription: string;
    priceMatchDescription: string;
    uploadDescription: string;
    modalTitle: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProduct;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

const mapDispatchToProps = {
    shareProduct,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

export interface ProductPriceMatchStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    subContainer?: GridContainerProps;
    subContainerHeader?: TypographyProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;

    firstNameTextField?: TextFieldProps;
    lastNameTextField?: TextFieldProps;
    emailTextField?: TextFieldProps;
    companyTextField?: TextFieldProps;
    websiteTextField?: TextFieldProps;
    phoneTextField?: TextFieldProps;
    commentsTextField?: TextAreaProps;
    consentCheckbox?: CheckboxProps;
    consentInfoLabel?: TypographyProps;
    formErrorLabel?: TypographyProps;

    reCaptchaGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    shareButton?: ButtonPresentationProps;
}

export const productShareLinkStyles: ProductPriceMatchStyles = {
    modal: {
        size: 800,
        cssOverrides: {
            modalTitle: css`
                padding: 10px 30px;
            `,
            modalContent: css`
                padding: 20px 30px;
            `,
        },
    },
    container: { gap: 10 },
    subContainer: {
        gap: 10,
        css: css`
            margin-top: 20px;
        `,
    },
    subContainerHeader: {
        weight: 600,
        size: "1.25em",
    },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding-left: 10px;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    consentCheckbox: {
        css: css`
            margin-top: 3px;
        `,
    },
    consentInfoLabel: {
        css: css`
            padding-left: 10px;
        `,
    },
    formErrorLabel: {
        color: "danger",
        weight: 600,
    },
    cancelButton: {
        variant: "secondary",
    },
    shareButton: {
        css: css`
            margin-left: 10px;
        `,
    },
};

const ProductPriceMatch = ({
    product,
    productInfo,
    text,
    modalTitle,
    session,
    validateReCaptcha,
    emailRecipients,
    priceMatchDescription,
    consentDescription,
    uploadDescription,
    extendedStyles,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productShareLinkStyles, extendedStyles));
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [firstName, setFirstName] = React.useState(isAuthenticated ? session.firstName : "");
    const [firstNameError, setFirstNameError] = React.useState("");
    const [lastName, setLastName] = React.useState(isAuthenticated ? session.lastName : "");
    const [lastNameError, setLastNameError] = React.useState("");
    const [email, setEmail] = React.useState(isAuthenticated ? session.email : "");
    const [emailError, setEmailError] = React.useState("");
    const [company, setCompany] = React.useState("");
    const [companyError, setCompanyError] = React.useState("");
    const [website, setWebsite] = React.useState("");
    const [websiteError, setWebsiteError] = React.useState("");
    const [phone, setPhone] = React.useState("");
    const [phoneError, setPhoneError] = React.useState("");
    const [comments, setComments] = React.useState("");
    const [consent, setConsent] = React.useState(false);
    const [consentError, setConsentError] = React.useState("");
    const [inProgress, setInProgress] = React.useState(false);
    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState("");
    const [files, setFiles] = React.useState<File[]>([]);
    const [phoneOnChange, setPhoneOnChange] = React.useState(new EventEx());
    const [phoneUpdate, setPhoneUpdate] = React.useState(new EventEx());
    const [phoneNumberFetched, setPhoneNumberFetched] = React.useState(false);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const getPhoneNumber = async () => {
        if (isAuthenticated) {
            if (session.shipToId !== undefined && session.billToId) {
                const getBillToParams: GetShipToApiParameter = {
                    billToId: session.billToId,
                    shipToId: session.shipToId,
                };
                const shipToModel = await getShipTo(getBillToParams);
                return shipToModel.phone;
            }
            return "";
        }
        return "";
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const priceMatchClickHandler = () => {
        resetFields();
        setModalIsOpen(true);
        setFirstName(session.firstName);
        setLastName(session.lastName);
        setEmail(session.email);
        getPhoneNumber().then(async r => {
            setPhone(PhoneNumberField.formatPhoneNumber(r, "000-000-0000"));
            setPhoneNumberFetched(true);
            await phoneUpdate.emitAsync();
        });
    };

    const requiredFieldMessage = translate("This is a required field.");
    const emailFieldMessage = translate("Enter a valid email address.");
    const consentMessage = translate("You must consent to the terms.");
    const incorrectFileExtensionMessage = translate("The file must be in a valid format.");
    const checkForErrors = async () => {
        const regexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
        let encounteredError = false;
        if (!regexp.test(email)) {
            setEmailError(emailFieldMessage);
            encounteredError = true;
        } else {
            setEmailError("");
        }

        if (!firstName || firstName === "") {
            setFirstNameError(requiredFieldMessage);
            encounteredError = true;
        } else {
            setFirstNameError("");
        }

        if (!lastName || lastName === "") {
            setLastNameError(requiredFieldMessage);
            encounteredError = true;
        } else {
            setLastNameError("");
        }

        if (!website || website === "") {
            setWebsiteError(requiredFieldMessage);
            encounteredError = true;
        } else {
            setWebsiteError("");
        }

        if (!company || company === "") {
            setCompanyError(requiredFieldMessage);
            encounteredError = true;
        } else {
            setCompanyError("");
        }

        await phoneUpdate.emitAsync();
        if (phoneError) {
            encounteredError = true;
        }

        if (!consent) {
            setConsentError(consentMessage);
            encounteredError = true;
        } else {
            setConsentError("");
        }

        let foundBadFile = false;
        for (const file of files) {
            const fileExtension = getFileExtension(file.name);
            const incorrectFileExtensionFlag =
                ["bmp", "jpeg", "jpg", "gif", "png", "pdf"].indexOf(fileExtension) === -1;
            if (incorrectFileExtensionFlag) {
                foundBadFile = true;
                encounteredError = true;
                setIncorrectFileExtension(incorrectFileExtensionMessage);
            }
        }
        if (!foundBadFile) {
            setIncorrectFileExtension("");
        }

        return encounteredError;
    };

    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(event.target.value);
    };
    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
    };
    const setEmailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
        setEmailError(!event.target.value ? emailFieldMessage : "");
    };
    const companyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompany(event.target.value);
    };
    const websiteChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setWebsite(event.target.value);
    };
    const commentsChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComments(event.target.value);
    };
    const filesChangeHandler = (event: any) => {
        const fileList: FileList = event.target.files;
        const files = [];
        for (let i = 0; i < fileList.length; i++) {
            const file = fileList.item(i);
            if (file) {
                files[i] = file;
            }
        }
        setFiles(files);

        let foundBadFile = false;
        for (const file of files) {
            const fileExtension = getFileExtension(file.name);
            const incorrectFileExtensionFlag =
                ["bmp", "jpeg", "jpg", "gif", "png", "pdf"].indexOf(fileExtension) === -1;
            if (incorrectFileExtensionFlag) {
                foundBadFile = true;
                setIncorrectFileExtension(incorrectFileExtensionMessage);
            }
        }
        if (!foundBadFile) {
            setIncorrectFileExtension("");
        }
    };
    const consentChangeHandler = (event: React.SyntheticEvent, value: boolean) => {
        setConsent(value);
        setConsentError(!value ? consentMessage : "");
    };

    const resetFields = () => {
        setFirstName("");
        setFirstNameError("");
        setLastName("");
        setLastNameError("");
        setEmail("");
        setEmailError("");
        setCompany("");
        setCompanyError("");
        setWebsite("");
        setWebsiteError("");
        setPhone("");
        setComments("");
        setConsent(false);
        setFiles([]);
        setConsentError("");
        setInProgress(false);
        setPhoneNumberFetched(false);
        setIncorrectFileExtension("");
    };

    const submitButtonClickHandler = async (addToast: Function) => {
        const successToast = {
            body: "Price Match Sent.",
            messageType: "info",
        };
        const errorToast = {
            body: "There was an error.",
            messageType: "danger",
        };

        const hasErrors = await checkForErrors();

        if (hasErrors) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "PriceMatchForm",
            recaptchaRef,
        });
        
        if (!isReCaptchaValid) {
            return;
        }

        setInProgress(true);

        const model = createModel();
        const request = sendPriceMatch(model, files);
        const error = checkStatusCode(request);
        if (!error) {
            setModalIsOpen(false);
            addToast(successToast);
        } else {
            addToast(errorToast);
        }
    };

    const createModel = () => {
        const model = new SendPriceMatchRequest();
        model.customer.firstName = firstName;
        model.customer.lastName = lastName;
        model.customer.email = email;
        model.customer.phone = phone;

        model.competitor.company = company;
        model.competitor.website = website;

        model.product.id = product.id;
        model.product.productNumber = product.productNumber;
        model.product.productDescription = product.productTitle;

        model.comments = comments;
        model.emailTos = emailRecipients.split("\n");

        return model;
    };

    useConstructor(() => {
        phoneOnChange.subscribe(async (phoneNumberState: PhoneNumberState) => {
            await setPhone(phoneNumberState.value);
            await setPhoneError(phoneNumberState.error);
        });
    });

    return (
        <>
            <Toaster>
                <ToasterContext.Consumer>
                    {({ addToast }) => (
                        <>
                            <Link onClick={priceMatchClickHandler} data-test-selector="productShareLink">
                                {text || translate("Price Match")}
                            </Link>
                            <Modal
                                {...styles.modal}
                                headline={translate(modalTitle)}
                                isOpen={modalIsOpen}
                                handleClose={modalCloseHandler}
                            >
                                <GridContainer {...styles.container} data-test-selector="productPriceMatchContactInfo">
                                    <GridItem {...styles.oneLinerGridItem}>
                                        <div>
                                            <Typography>{priceMatchDescription}</Typography>
                                        </div>
                                    </GridItem>
                                    <GridContainer
                                        {...styles.subContainer}
                                        data-test-selector="productPriceMatchContactInfo"
                                    >
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <Typography {...styles.subContainerHeader}>Contact Information</Typography>
                                        </GridItem>
                                        <GridItem {...styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("First Name")}
                                                value={firstName}
                                                onChange={firstNameChangeHandler}
                                                data-test-selector="productPriceMatchFirstName"
                                                error={firstNameError}
                                                required={true}
                                                {...styles.firstNameTextField}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Last Name")}
                                                value={lastName}
                                                onChange={lastNameChangeHandler}
                                                data-test-selector="productPriceMatchLastName"
                                                error={lastNameError}
                                                required={true}
                                                {...styles.lastNameTextField}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Email")}
                                                value={email}
                                                onChange={setEmailChangeHandler}
                                                data-test-selector="productPriceMatchEmail"
                                                error={emailError}
                                                required={true}
                                                {...styles.emailTextField}
                                            />
                                        </GridItem>
                                        {phoneNumberFetched && <GridItem {...styles.twoLinerGridItem}>
                                            <PhoneNumberField
                                                label={translate("Phone")}
                                                initialValue={phone}
                                                onChange={phoneOnChange}
                                                update={phoneUpdate}
                                                required={false}
                                                format="000-000-0000"
                                                hideRequiredAsterisk={false}
                                            />
                                        </GridItem>
                                        }
                                    </GridContainer>
                                    <GridContainer
                                        {...styles.subContainer}
                                        data-test-selector="productPriceMatchContactInfo"
                                    >
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <Typography {...styles.subContainerHeader}>Competitor Information</Typography>
                                        </GridItem>
                                        <GridItem {...styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Who are you buying this item from?")}
                                                value={company}
                                                onChange={companyChangeHandler}
                                                error={companyError}
                                                data-test-selector="productPriceMatchCompany"
                                                required={true}
                                                {...styles.companyTextField}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Website")}
                                                value={website}
                                                onChange={websiteChangeHandler}
                                                error={websiteError}
                                                data-test-selector="productPriceMatchWebsite"
                                                required={true}
                                                {...styles.websiteTextField}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Product Item Code")}
                                                value={product.productNumber}
                                                disabled={true}
                                                data-test-selector="productPriceMatchProductNumber"
                                                required={true}
                                                {...styles.websiteTextField}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Product Details")}
                                                value={product.productTitle}
                                                disabled={true}
                                                data-test-selector="productPriceMatchProductDetails"
                                                required={true}
                                                {...styles.websiteTextField}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <TextArea
                                                label={translate("Comments")}
                                                value={comments}
                                                onChange={commentsChangeHandler}
                                                data-test-selDector="productPriceMatchComments"
                                                {...styles.commentsTextField}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                    <GridContainer
                                        {...styles.subContainer}
                                        data-test-selector="productPriceMatchContactInfo"
                                    >
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <Typography {...styles.subContainerHeader}>File Upload</Typography>
                                        </GridItem>
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <FileUpload
                                                label="Upload File"
                                                buttonProps={{
                                                    icon: { src: Upload, position: "left" },
                                                }}
                                                buttonText="Image/PDF"
                                                accept=".bmp,.jpeg,.jpg,.gif,.png,.pdf"
                                                error={incorrectFileExtension}
                                                border="underline"
                                                onFileChange={filesChangeHandler}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <Typography>{uploadDescription}</Typography>
                                        </GridItem>
                                    </GridContainer>
                                    <GridContainer
                                        {...styles.subContainer}
                                        data-test-selector="productPriceMatchContactInfo"
                                    >
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <Checkbox
                                                checked={consent}
                                                onChange={consentChangeHandler}
                                                data-test-selector="productPriceMatchConsent"
                                                error={consentError !== ""}
                                                {...styles.consentCheckbox}
                                            />
                                            <Typography {...styles.consentInfoLabel}>{consentDescription}</Typography>
                                        </GridItem>
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <Typography {...styles.formErrorLabel} hidden={consentError === ""}>
                                                {consentError}
                                            </Typography>
                                        </GridItem>
                                    </GridContainer>
                                </GridContainer>
                                <StyledWrapper {...styles.buttonsWrapper}>
                                    <Button
                                        {...styles.cancelButton}
                                        onClick={modalCloseHandler}
                                        data-test-selector="productShareCancel"
                                    >
                                        {translate("Cancel")}
                                    </Button>
                                    <Button
                                        {...styles.shareButton}
                                        onClick={() => {
                                            submitButtonClickHandler(addToast);
                                        }}
                                        data-test-selector="productShareSubmit"
                                        disabled={inProgress}
                                    >
                                        {translate("Submit")}
                                    </Button>
                                </StyledWrapper>
                            </Modal>
                        </>
                    )}
                </ToasterContext.Consumer>
            </Toaster>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductPriceMatch));
